import { BookingLiteModel, BookingStatusEnum } from 'api-hooks/booking/model';
import { wjlTimeConversion } from 'common/utils/date';
import Ticket from 'modules/check-booking/ticket';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useDialog from './use-dialog';

type BookingType = 'active' | 'history' | 'pending' | 'refund';

export default function useMappingBookingItems(
  bookings: BookingLiteModel[],
  type: BookingType,
) {
  const dialog = useDialog();
  const { t } = useTranslation();
  const onClickTicket = React.useCallback(
    (booking: BookingLiteModel) => () => {
      dialog.showCustom({
        render(close) {
          return (
            <Ticket
              onClose={close}
              bookingId={booking.id}
              cruiseName={booking.ship}
            />
          );
        },
      });
    },
    [dialog],
  );

  return bookings.map((booking) => {
    let passenger = '';

    if (booking.passengerAdultCount) {
      passenger += t('booking:adult_extra', {
        extra: booking?.passengerAdultCount,
      });
    }

    if (booking.passengerInfantCount) {
      passenger += ' ';
      passenger += t('booking:infant_extra', {
        extra: booking?.passengerInfantCount,
      });
    }

    const arrivalDayCycle = booking.arrivedAt.dayCycle;
    const arrivalDate = wjlTimeConversion(booking.arrivedAt);
    const arrivalTime = booking.arrivedAt.time!;
    let departureDayCycle = booking.departureAt.dayCycle;
    let departureDate = wjlTimeConversion(booking.departureAt);

    if (booking.delayedDepartureAt.date) {
      departureDate = wjlTimeConversion(booking.delayedDepartureAt);
    }
    let departureTime = booking.departureAt.time!;
    if (booking.delayedDepartureAt.time) {
      departureTime = booking.delayedDepartureAt.time!;
      departureDayCycle = booking.delayedDepartureAt.dayCycle;
    }

    let timer: Date | undefined = undefined;

    if (booking.status === BookingStatusEnum.PROCESSING) {
      if (new Date().getTime() < booking.expiredAt?.getTime()) {
        timer = booking.expiredAt;
      }
    }

    return {
      id: booking.id,
      cruiseName: booking.ship,
      bookingId: booking.id,
      destinationLocation: booking.toCity,
      destinationPort: booking.toHarbor,
      price: booking?.totalPrice?.toString(),
      sourceLocation: booking.fromCity,
      sourcePort: booking.fromHarbor,
      refundStatus: booking.refundStatus,
      ticketId: booking.id,
      code: booking.code,
      departureTz: booking.departureTz.toUpperCase(),
      arrivalTz: booking.arrivedTz.toUpperCase(),
      status: booking.status,
      voyageCode: booking.voyageCode,
      refundRejectedReason: booking.refundRejectedReason,
      vehicle:
        booking?.vehicleCount
          ?.map((vehicle) => {
            const labels = [vehicle.name];
            if (typeof vehicle.maxHeight === 'string' && !!vehicle.maxHeight) {
              labels.push(
                t('booking:max_height_extra', { extra: vehicle.maxHeight }),
              );
            }
            labels.push(`(x${vehicle.qty})`);
            return labels.join(' ');
          })
          ?.join(', ') || '-',
      onClickTicket: onClickTicket(booking),
      passenger,
      arrivalTime,
      departureDate,
      departureTime,
      arrivalDayCycle,
      departureDayCycle,
      arrivalDate,
      timer,
      type,
    };
  });
}
